<template>
  <div class="wrap" :class="{'wrap_flex': !loaded, 'no-scroll': showPushUnblockPopup}">
    <div class="container" v-if="loaded">
      <the-header :stages="stages">{{ headerText }}</the-header>
      <div
        :class="{
          'body-block_stage1': stages[0].active,
          'body-block_stage2': stages[1].active,
        }"
        class="body-block">
        <template v-if="stages[0].active">
          <div class="coin-info">
            <div class="coin-info__col coin-info__col_selector">
              <currency-selector @input="changeCurrency" v-model="selectedCurrency" />
            </div>
            <div class="coin-info__col">
              <div class="coin-info__name">Price, 24h change</div>
              <div class="coin-info__val-wrap">
                <div class="coin-info__value">{{selectedCurrency | getObjPropInSelectedCurrency('price', alertCurrencyCode) | formatMoney(alertCurrencyCode)}}</div>
                <percent-trend :value="selectedCurrency | getObjPropInSelectedCurrency('percentChange24h', alertCurrencyCode)" class="coin-info__difference" />
              </div>
            </div>
            <div class="coin-info__col">
              <div class="coin-info__name">Market cap, 24h change</div>
              <div class="coin-info__val-wrap">
                <div class="coin-info__value">{{selectedCurrency | getObjPropInSelectedCurrency('marketCapChange24h', alertCurrencyCode) | formatMoney(alertCurrencyCode)}}</div>
                <percent-trend :value="selectedCurrency | getObjPropInSelectedCurrency('marketCapPercentChange24h', alertCurrencyCode)" class="coin-info__difference" />
              </div>
            </div>
            <div class="coin-info__col">
              <div class="coin-info__name">24h volume</div>
              <div class="coin-info__val-wrap">
                <div class="coin-info__value">{{selectedCurrency | getObjPropInSelectedCurrency('marketVol24h', alertCurrencyCode) | formatMoney(alertCurrencyCode)}}</div>
              </div>
            </div>
          </div>
          <form novalidate class="fields fields_stage1">
            <div class="fields__col1">
              <div class="fields__item">
                <info-field label="Current value" :text="valueParameter | formatMoney(alertCurrencyCode)" />
              </div>
              <div class="fields__item fields__item_value-types value-types">
                <radio-el valueIsObj inline v-model="form.valueType" :radioValues="valueTypes" />
              </div>
              <div class="fields__item fields__item_alert-types alert-types">
                <radio-el inline v-model="form.alertType" :radioValues="conditionTypes" />
              </div>
            </div>
            <div class="fields__col2">
              <div class="fields__item">
                <info-field
                  label="Alert value"
                  :text="alertValueText"
                  :loading="infoFieldIsLoading"
                  :hasError="$v.form.targetDelta.$error"
                />
              </div>
              <div class="fields__item fields__item_target-value" >
                <target-value-input
                  :showPrecisionTooltip="showPrecisionTooltip"
                  :availablePrecision="availablePrecision"
                  :valueTypeName="form.valueType.value"
                  :alertType="alertType"
                  :tickerName="textPreview && textPreview.tickerName"
                  @input="serverFieldErrors.targetDelta = null"
                  @toggleDirection="toggleAlertDireciton"
                  :error="$v.form.targetDelta.$error"
                  v-model.trim="$v.form.targetDelta.$model"
                />
              </div>
            </div>
          </form>
        </template>
        <template v-else-if="stages[1].active">
          <form novalidate data-vv-scope="step2">
            <div class="fields fields_stage2">
              <div class="channels">
                <div class="channels__title">Channels</div>
                <div class="channels__items">
                  <div class="channels__col channels__col_first">
                    <checkbox-el :disabled="notificationsIsBlocked || pushNotificationsIsNotSupported" class="channels__item" v-model="form.withPush" id="pushCheckbox">Push notifications</checkbox-el
                      ><tooltip class="tooltip tooltip_push-notifications">
                        <div class="tooltip-icons">
                          <icon-base white class="icon-chrome">
                            <icon-chrome/>
                          </icon-base>
                          <icon-base white class="icon-firefox">
                            <icon-firefox/>
                          </icon-base>
                        </div>
                        You can use this feature in Chrome and Firefox browsers.
                      </tooltip
                      ><checkbox-el
                      class="channels__item"
                      v-model="form.withEmail"
                      id="emailCheckbox"
                      :disabled="!confirmationData.isEmailConfirmed"
                    >Email</checkbox-el>
                  </div>
                  <div class="channels__col">
                    <checkbox-el
                      class="channels__item"
                      v-model="form.withSMS"
                      id="smsCheckbox"
                      :disabled="!confirmationData.isPhoneConfirmed"
                    >SMS</checkbox-el>
                    <div class="channels__item-wrap">
                      <checkbox-el class="channels__item" id="webhookCheckbox" v-model="form.withWebhook">
                        Webhook
                      </checkbox-el
                      ><tooltip class="tooltip">
                        It is a method of augmenting the behaviour of a web page with custom callbacks. These callbacks may be maintained, modified, and managed by third-party users and developers.
                      </tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div class="type">
                <div class="type__title">Type</div>
                <div class="type__items">
                  <radio-el v-model="form.frequency" :radioValues="radioValues" class="type__radio"/>
                </div>
              </div>
              <div class="line-break"></div>
              <div class="webhook-input" v-if="form.withWebhook">
                <input-el
                  type="text"
                  placeholder="URL"
                  label="URL for webhook"
                  inputId="webhookUrl"
                  name="webhookUrl"
                  @input="serverFieldErrors.webhook = null"
                  v-model.trim="$v.form.webhook.$model"
                  :error="$v.form.webhook.$error"
                  errorBottom
                  small
                />
              </div>
            </div>
          </form>
          <red-label class="alert alert_sms" v-if="confirmationData && !confirmationData.isPhoneConfirmed">
            <div class="alert__text">
              SMS channel is disabled because your phone is not confirmed
            </div>
            <button-el @click="openPhoneChangePopup" class="alert__btn" red>CONFIRM PHONE</button-el>
          </red-label>
          <red-label class="alert alert_email" v-if="confirmationData && !confirmationData.isEmailConfirmed">
            <div class="alert__text">
              Email channel is disabled because your email is not confirmed
            </div>
            <button-el class="alert__btn" red>CONFIRM EMAIL</button-el>
          </red-label>
          <red-label class="alert alert_push" v-if="notificationsIsBlocked && !pushNotificationsIsNotSupported">
            <div class="alert__text">
              Push channel disabled because you blocked push notifications in browser
            </div>
            <button-el class="alert__btn" @click="showPushUnblockPopup = true" red>UNBLOCK</button-el>
          </red-label>
        </template>
        <text-preview
          :formData="form"
          :previewData="textPreview"
          :currencyCode="alertCurrencyCode"
        />
      </div>
      <div class="footer-block">
        <div v-if="stages[0].active" class="buttons">
          <router-link :to="cancelBtnUrl" class="buttons__item-link">
            <button-el class="buttons__item" black left>
              Cancel
              <icon-base slot="icon" black>
                <icon-cross />
              </icon-base>
            </button-el>
          </router-link>
          <button-el :disabled="step2Loading || $v.form.targetDelta.$error" class="buttons__item" @click="tryToGoNextStage" red left>
            Continue
            <icon-base slot="icon" red>
              <icon-arrow-min />
            </icon-base>
          </button-el>
        </div>
        <div v-else-if="stages[1].active" class="buttons">
          <button-el class="buttons__item" @click="goPrevStage" black left>
            Back
            <icon-base slot="icon" black>
              <icon-arrow-min left />
            </icon-base>
          </button-el>
          <button-el :disabled="sendingIsDisabled" v-if="modeIsEditing" @click="tryToUpdateAlert" class="buttons__item" red left>
            Update
            <icon-base slot="icon" red>
              <icon-check left />
            </icon-base>
          </button-el>
          <button-el :disabled="sendingIsDisabled" v-else @click="tryToCreateAlert" class="buttons__item" red left>
            Create
            <icon-base slot="icon" red>
              <icon-check left />
            </icon-base>
          </button-el>
        </div>
      </div>
    </div>
    <spinner-el v-else />
    <phone-change-popup
      v-if="showPhoneChangePopup"
      @closePopupWithNoResult="closePhoneChangePopup"
      @closePopupWithSucccess="recheckPhoneAndClosePopup"
    />
    <push-unblock-popup
      v-if="showPushUnblockPopup"
      @closePopup="showPushUnblockPopup = false"
    />
  </div>
</template>

<script>
  /* @flow */
  import debounce from 'lodash/debounce';
  import firebase from 'firebase/app'; //eslint-disable-line
  import 'firebase/messaging'; //eslint-disable-line
  import { mapState, mapActions, mapGetters } from 'vuex';
  import { required, between, requiredIf } from 'vuelidate/lib/validators'; //eslint-disable-line
  import {
    formatMoney,
    beautifyTargetValue,
    getObjPropInSelectedCurrency,
    checkNumHasAvailablePrecision,
    checkAlertTypeIsPercentage,
    targetDeltaValidator,
  } from 'src/utils/helpers';
  import checkMobileVersion from 'src/mixins/checkMobileVersion';
  import { prevUrlLocalStorageName, messagingSenderId, publicVapidKey } from 'src/utils/config';
  import { conditionOptions, conditionTypes, conditionDirections, upDirection, downDirection } from 'src/models/AlertsConditionTypes'; // eslint-disable-line
  import { valueTypes, marketCapValueType, marketVol24ValueType } from 'src/models/AlertsValueTypes';
  import validateFormMixin from 'src/mixins/validateForm';
  import stagesSwitchersMixin from 'src/mixins/stagesSwitchers';
  import saveAlertDataToLocalStorageMixin from 'src/mixins/saveAlertDataToLocalStorage';
  import AlertManipulationModes from 'src/models/AlertManipulationModes';
  import loginModalTabs from 'src/models/LoginModalTabs';
  import AlertCreationForm from 'src/models/AlertCreationForm';
  import { IconCross, IconCheck, IconArrowMin, IconChrome, IconFirefox } from 'Icons';
  import phoneChangePopupLogic from 'src/mixins/phoneChangePopupLogic';
  import PushUnblockPopup from './PushUnblockPopup';
  import TextPreview from './TextPreview';
  import TheHeader from './TheHeader';
  import TargetValueInput from './TargetValueInput';

  export default {
    mixins: [
      validateFormMixin,
      stagesSwitchersMixin,
      saveAlertDataToLocalStorageMixin,
      phoneChangePopupLogic,
      checkMobileVersion,
    ],
    components: {
      IconCross,
      IconArrowMin,
      TheHeader,
      IconCheck,
      IconChrome,
      IconFirefox,
      PushUnblockPopup,
      TextPreview,
      TargetValueInput,
    },
    props: {
      tickerId: {
        type: Number,
      },
      mode: String,
      alertId: Number,
    },
    data: () => ({ // eslint-disable-line
      serverFieldErrors: {
        targetDelta: null,
        webhook: null,
      },
      textPreviewUpdated: false,
      notificationsIsBlocked: false,
      pushNotificationsIsNotSupported: false,
      cancelBtnUrl: '/',
      selectedCurrency: null,
      sendingIsDisabled: false,
      confirmationData: {
        isEmailConfirmed: false,
        isPhoneConfirmed: false,
      },
      textPreview: null,
      step2Loading: false,
      form: new AlertCreationForm(),
      loaded: false,
      stages: [
        {
          name: 'Configuration',
          finished: false,
          active: true,
        },
        {
          name: 'Channels & Frequency',
          finished: false,
          active: false,
        },
      ],
      showPushUnblockPopup: false,
    }),
    validations: {
      form: {
        targetDelta: {
          required,
          between: between(Number.MIN_VALUE, Number.MAX_SAFE_INTEGER),
          serverValidity(): boolean {
            return !this.serverFieldErrors.targetDelta;
          },
          constraintValue(): boolean {
            return targetDeltaValidator(
              this.form.targetDelta,
              this.valueParameter,
              this.alertTypeIsPercentage,
              this.alertTypeIsDecrease,
            );
          },
        },
        webhook: {
          requiredIf: requiredIf((model: Object): boolean => model.withWebhook),
          serverValidity(): boolean {
            return !this.serverFieldErrors.webhook;
          },
        },
      },
      step1: ['form.targetDelta'],
      step2: ['form.webhook'],
    },
    filters: {
      formatMoney,
      getObjPropInSelectedCurrency,
    },
    methods: {
      ...mapActions('settings', ['toggleLoginModal']),
      toggleAlertDireciton() {
        if (this.form.alertDirection === upDirection) {
          this.form.alertDirection = downDirection;
        } else {
          this.form.alertDirection = upDirection;
        }
      },
      requestPushPermission() {

      },
      toggleSubscribeToPush(enable: boolean) {
        if (!enable) {
          return;
        }
        this.sendingIsDisabled = true;
        // send request for getting pushes
        let messaging;
        try {
          messaging = firebase.messaging();
        } catch (error) {
          this.pushNotificationsIsNotSupported = true;
          this.sendingIsDisabled = false;
          return;
        }
        messaging.requestPermission().then((): any => { // eslint-disable-line arrow-body-style
          return messaging.getToken().then((currentToken: string): any => {
            if (!currentToken) {
              return Promise.reject('Can\'t receive token');
            }
            this.form.deviceToken = currentToken;
            return Promise.resolve();
          }).catch((err: string): any => { // eslint-disable-line arrow-body-style
            this.pushNotificationsIsNotSupported = true;
            return Promise.reject(`Error during token receiving ${err}`);
          });
        }).catch((err: Object) => {
          console.warn('Can\'t get approve on push notifications.', err);//eslint-disable-line
          this.form.withPush = false;
        }).finally(() => {
          this.sendingIsDisabled = false;
        });
      },
      initFirebase() {
        try {
          const config = {
            messagingSenderId,
          };
          firebase.initializeApp(config);
          const messaging = firebase.messaging();
          messaging.usePublicVapidKey(publicVapidKey); // eslint-disable-line
        } catch (err) {
          const initializedCode = 'app/duplicate-app';
          if (err.code === initializedCode) {
            // already initialized
          } else {
            this.pushNotificationsIsNotSupported = true;
            console.warn(err); //eslint-disable-line
          }
        }
      },
      recheckPhoneAndClosePopup() {
        this.getUserConfirmationStatus();
        this.closePhoneChangePopup();
      },
      /** Check form on errors and shows them, if there are no errors, check user authorization.
       *  If user isn't authorized, shows login modal him. Otherwise opens next stage.
       */
      tryToGoNextStage() {
        this.validateForm('step1', () => {
          if (this.loginInfo) {
            this.step2Loading = true;
            this.getUserConfirmationStatus()
            .then(this.goNextStage)
            .finally(() => {
              this.step2Loading = false;
            });
          } else {
            if (this.isMobile) { //eslint-disable-line
              this.$router.push(`/authorization-page?returnUrl=${this.$route.fullPath}`);
            } else {
              this.toggleLoginModal({ val: true, activeTab: loginModalTabs.login });
            }
          }
        });
      },
      tryToGetPreviewMessage() {
        if (!this.selectedCurrency) {
          return;
        }
        this.validateForm('step1', this.getPreviewMessage);
      },
      changeCurrency(currency: iCurrency) {
        if (this.modeIsCreating) {
          this.$router.replace(`/add-alert/${currency.id}`, () => {
            this.saveAlertDataToLocalStorage(this.form, currency.id);
          });
        } else {
          this.textPreviewUpdated = false;
          this.debounceGetPreviewMessage();
        }
      },
      loadAlertDataFromLocalStorage() {
        const data = this.getAlertsDataFromLocalStorage();
        if (data[this.tickerId]) {
          this.form = data[this.tickerId];
        }
      },
      getPreviewMessage(): Promise<void> {
        return this.$apiService.getPreviewMessageAlertCondition(this.dataForTextPreview).then((data) => { // eslint-disable-line
          this.textPreview = data;
          this.textPreviewUpdated = true;
        });
      },
      getCurrency(tickerId: number): Promise<void> {
        return this.$apiService.getTickerByTickerId(tickerId).then((currency: iCurrency) => { // eslint-disable-line
          this.selectedCurrency = currency;
          if (this.modeIsCreating) {
            this.loadAlertDataFromLocalStorage();
            if (!this.form.frequency) {
              this.form.frequency = this.radioValues[0].value;
            }
            if (!this.alertType) {
              this.alertType = conditionOptions[0];
            }
          }
        }).catch((): Promise<void> => Promise.reject());
      },
      getUserConfirmationStatus(): Promise<void> {
        return this.$apiService.getUserConfirmationStatus().then((data: iConfirmationStatus) => {
          this.confirmationData = data;
        });
      },
      tryToCreateAlert() {
        this.validateForm('step2', () => {
          this.$apiService.createAlert(this.alertDataForSending).then(() => {
            this.$router.push('/dashboard');
          }).catch(this.handleAlertServerError);
        });
      },
      tryToUpdateAlert() {
        this.validateForm('step2', () => {
          this.$apiService.updateAlert(this.alertId, this.alertDataForSending).then(() => {
            this.$router.push('/dashboard');
          }).catch(this.handleAlertServerError);
        });
      },
      handleAlertServerError({ fieldErrors }: Object) {
        if (!fieldErrors) {
          return;
        }
        if (fieldErrors.targetDelta) {
          this.goPrevStage();
        }
        if (fieldErrors.webhook) {
          this.form.withWebhook = true;
        }
        this.serverFieldErrors = fieldErrors;
      },
      getAlert(): Promise<void> {
        return this.$apiService.getAlertById(this.alertId).then((data: iAlertServer): Promise<void> => { // eslint-disable-line
          this.alertType = conditionOptions.find((p: iOption): boolean => p.name === data.alertType);
          this.form.valueType = valueTypes.find((p: iOption): boolean => p.name === data.valueType);
          this.form.frequency = data.frequency;
          this.form.withEmail = data.withEmail;
          this.form.withSMS = data.withSMS;
          this.form.withPush = data.withPush;
          this.form.currencyCode = data.currencyCode;
          this.form.withWebhook = data.withWebhook;
          this.form.targetDelta = beautifyTargetValue(data.targetValue, data.alertType);
          this.form.webhook = data.webhook;
          return this.getCurrency(data.tickerId);
        });
      },
      checkNotificationsPermissionStatus() {
        const notification = window.Notification || window.mozNotification || window.webkitNotification;  // eslint-disable-line max-len
        if (!notification || !notification.permission) {
          this.pushNotificationsIsNotSupported = true;
          return;
        }
        const notificationsIsBlocked = notification.permission === 'denied';
        if (this.notificationsIsBlocked !== notificationsIsBlocked) {
          this.notificationsIsBlocked = notificationsIsBlocked;
        }
      },
      watchNotificationsPermissionStatusUpdate() {
        this.notificationsWatcherTimerId = setTimeout(() => {
          this.checkNotificationsPermissionStatus();
          this.watchNotificationsPermissionStatusUpdate();
        }, 400);
      },
      unwatchNotificationsPermissionStatusUpdate() {
        clearTimeout(this.notificationsWatcherTimerId);
      },
      formatTargetValueForSending(val: number, alertType: string): number {
        if (checkAlertTypeIsPercentage(alertType)) {
          return val / 100;
        }
        return +val;
      },
    },
    computed: {
      ...mapState('userData', ['loginInfo']),
      ...mapGetters('userData', ['currencyCode']),
      valueTypes: () => valueTypes, // eslint-disable-line
      conditionTypes: () => conditionTypes, // eslint-disable-line
      conditionDirections: () => conditionDirections, // eslint-disable-line
      alertCurrencyCode(): boolean {
        return this.modeIsCreating ? this.currencyCode : this.form.currencyCode;
      },
      alertValueText(): string {
        if (!this.textPreview) {
          return '';
        }
        return formatMoney(this.textPreview.targetValue, this.alertCurrencyCode);
      },
      showPrecisionTooltip(): boolean {
        if (this.$v.form.targetDelta.$error || !this.textPreview || !this.textPreviewUpdated) {
          return false;
        }
        return this.precisionMoreThanTargetDelta;
      },
      radioValues: () => [ // eslint-disable-line
        {
          name: 'one_time_event',
          id: 'one_time_event',
          value: 'one_time_event',
          text: 'One time',
        },
        {
          name: 'persistent',
          id: 'persistent',
          value: 'persistent',
          text: 'Persistent',
          tooltip: 'Alert of this type remains always active. After each trigger it is automatically recreated with the updated set of parameters.',
        },
      ],
      alertType: {
        get(): iConditionOption {
          const { alertType, alertDirection } = this.form;
          return conditionOptions.find(o => o.type === alertType && o.direction === alertDirection);// eslint-disable-line
        },
        set(val: iConditionOption) {
          this.form.alertType = val.type;
          this.form.alertDirection = val.direction;
        },
      },
      alertDataForSending(): iAlertServer {
        const form = { ...this.form };
        form.targetDelta = this.formatTargetValueForSending(this.form.targetDelta, this.alertType.name);// eslint-disable-line max-len
        form.alertType = this.alertType.name;
        form.valueType = this.form.valueType.name;
        form.tickerId = this.selectedCurrency.id;
        form.baseValue = this.valueParameter;
        if (!form.withWebhook) {
          form.webhook = null;
        }
        return form;
      },
      alertTypeIsPercentage(): boolean {
        return checkAlertTypeIsPercentage(this.alertType.name);
      },
      valueParameter(): string {
        const { fieldName } = this.form.valueType;
        return getObjPropInSelectedCurrency(this.selectedCurrency, fieldName, this.alertCurrencyCode) || '';
      },
      dataForTextPreview(): any {
        const { targetDelta } = this.form;
        const fixedTd = this.alertTypeIsPercentage ? targetDelta / 100 : targetDelta;
        const data = {
          valueType: this.form.valueType.name,
          alertType: this.alertType.name,
          targetDelta: +fixedTd,
          tickerId: (this.selectedCurrency && this.selectedCurrency.id) || this.tickerId,
          currentPrice: this.valueParameter,
          currencyCode: this.alertCurrencyCode,
        };
        return data;
      },
      modeIsCreating(): boolean {
        return this.mode === AlertManipulationModes.creating;
      },
      modeIsRepeating(): boolean {
        return this.mode === AlertManipulationModes.repeating;
      },
      modeIsEditing(): boolean {
        return this.mode === AlertManipulationModes.editing;
      },
      headerText(): string {
        if (this.modeIsCreating) return 'Alert Creation';
        if (this.modeIsRepeating) return 'Alert Repeating';
        if (this.modeIsEditing) return 'Alert Editing';
        return '';
      },
      precisionMoreThanTargetDelta(): boolean {
        if (!this.availablePrecision) {
          return false;
        }
        const { targetDelta } = this.form;
        return !checkNumHasAvailablePrecision(targetDelta, this.availablePrecision);
      },
      availablePrecision(): number {
        if (this.alertTypeIsPercentage) {
          return 2;
        }
        const valueTypeIsMarketcap = this.form.valueType === marketCapValueType;
        const valueTypeIsVolume = this.form.valueTypes === marketVol24ValueType;
        if (valueTypeIsMarketcap || valueTypeIsVolume) {
          return 4;
        }
        return this.textPreview && this.textPreview.availablePrecision;
      },
      alertTypeIsDecrease(): boolean {
        return this.alertType.direction === downDirection;
      },
      infoFieldIsLoading(): boolean {
        return !this.textPreviewUpdated && !this.$v.form.targetDelta.$error;
      },
    },
    watch: {
      currencyCode() {
        this.textPreviewUpdated = false;
        this.debounceGetPreviewMessage();
      },
      notificationsIsBlocked(val: boolean) {
        if (!val) {
          this.showPushUnblockPopup = false;
        } else {
          this.form.withPush = false;
        }
      },
      confirmationData: {
        handler(data: iConfirmationStatus) {
          if (this.form.withEmail) {
            this.form.withEmail = data.isEmailConfirmed;
          }
          if (this.form.withSMS) {
            this.form.withSMS = data.isPhoneConfirmed;
          }
        },
        deep: true,
      },
      loginInfo(loggedIn: Object) {
        if (!loggedIn) {
          this.goPrevStage();
        }
      },
      form: {
        handler(val: iAlertForm) {
          if (this.modeIsCreating) {
            this.saveAlertDataToLocalStorage(val);
          }
          if (this.currencyCode) {
            this.textPreviewUpdated = false;
            this.debounceGetPreviewMessage();
          }
        },
        deep: true,
      },
      'form.withPush'(val: boolean) { // eslint-disable-line object-shorthand
        this.toggleSubscribeToPush(val);
      },
      tickerId() {
        this.textPreviewUpdated = false;
        this.getCurrency(this.tickerId);
        this.debounceGetPreviewMessage();
      },
    },
    created() {
      this.initFirebase();
      const delayBetweenRequests = 300;
      this.debounceGetPreviewMessage = debounce(this.tryToGetPreviewMessage, delayBetweenRequests);
      const request = this.modeIsCreating ? this.getCurrency(this.tickerId) : this.getAlert();
      request.then(() => {
        this.textPreviewUpdated = false;
        this.debounceGetPreviewMessage();
        this.loaded = true;
      });
      const prevUrl = localStorage.getItem(prevUrlLocalStorageName);
      this.cancelBtnUrl = prevUrl || this.cancelBtnUrl;
      this.watchNotificationsPermissionStatusUpdate();
    },
    beforeDestroy() {
      this.unwatchNotificationsPermissionStatusUpdate();
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .wrap {
    @include background;
    &_flex {
      display: flex;
    }
    &.no-scroll {
      overflow: hidden;
    }
  }
  .container {
    @include container;
    border-radius: 5px;
    width: calc(100% - 20px);
    background: $white-color2;
  }
  $padding: 30px;
  .body-block {
    padding: $padding $padding 10px;
  }
  .footer-block {
    padding: 20px $padding 45px;
  }
  $table-border-radius: 6px;
  .coin-info {
    width: 100%;
    display: flex;
    &__col {
      border: $common-border;
      border-right: none;
      &:first-child {
        border-radius: $table-border-radius 0 0 $table-border-radius;
      }
      &:last-child {
        border-radius: 0 $table-border-radius $table-border-radius 0;
        border-right: $common-border;
      }
      height: 120px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 0 17px 0 29px;
      flex-direction: column;
      width: 279px;
      &_selector {
        width: 299px;
      }
    }
    &__name {
      @include font(12px, $grey-color1);
      font-weight: 500;
      margin-bottom: 9px;
      margin-top: 4px;
    }
    &__value {
      @include font(17px);
      line-height: 27px;
      font-weight: 500;
      display: inline-block;
      margin-right: 4px;
    }
    &__val-wrap .value {
      @include valueLabel;
      &_red {
        &:before {
          background: $red-color3;
        }
      }
      &_green {
        &:before {
          background: $green-color1;
        }
      }
    }
    &__amount {
      @include font(17px);
      line-height: 27px;
      font-weight: 500;
      display: inline-block;
      margin-top: 4px;
    }
    &__difference {
      @include font(13px, null);
      font-weight: 500;
      display: inline-block;
      margin-left: 3px;
    }
    &__difference_decreased {
      color: $red-color3;
    }
    &__difference_increased {
      color: $green-color1;
    }
  }
  .fields {
    display: flex;
    padding: 38px 31px;
    &__col1 {
      margin-right: 214px;
    }
    &__col1,
    &__col2 {
      display: flex;
      flex-direction: column;
    }
    &__item {
      position: relative;
      z-index: 1;
      &_value-types {
        margin-top: 31px;
        position: relative;
        &:after {
          content: '';
          display: block;
          position: absolute;
          width: 100%;
          height: 1px;
          background: $white-color1;
          bottom: 1px;
        }
      }
      &_alert-types {
        margin-top: 16px;
      }
      &_target-value {
        margin-top: 31px;
      }
    }
    &_stage2 {
      justify-content: flex-start;
      flex-wrap: wrap;
      padding: 28px 0px;
      margin-bottom: 14px;
    }
  }
  .buttons {
    $margin: 15px;
    &__item {
      max-width: 180px !important;
      &:not(:last-child) {
        margin-right: $margin;
      }
    }
    &__item-link {
      margin-right: $margin;
    }
  }
  @mixin title {
    font-weight: 500;
    font-size: 17px;
  }
  .channels {
    display: flex;
    margin-bottom: 20px;
    $margin-top: 18px;
    &__items {
      display: flex;
      flex-wrap: wrap;
      margin-left: -9px;
      margin-top: -$margin-top;
    }
    &__item {
      margin-top: $margin-top;
      display: inline-block;
    }
    &__title {
      @include title;
    }
    &__col {
      width: 196px;
      padding-left: 31px;
      &_first {
        width: 226px;
        padding-left: 39px;
      }
    }
  }
  .tooltip {
    margin-left: 7px;
    vertical-align: middle;
    display: inline-block;
  }
  .alert {
    max-width: 677px;
    padding-right: 25px !important;
    margin-bottom: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &_email {
      margin-top: 20px;
    }
    &__btn {
      margin-left: 27px;
      background-color: transparent;
    }
    &__text {
      line-height: 18px;
    }
    &_push {
      width: 784px;
      .btn {
        margin-left: 53px;
      }
    }
  }
  .type {
    display: flex;
    margin-left: 16px;
    &__title {
      @include title;
    }
    &__items {
      margin-left: 30px;
    }
    &__radio {
      font-size: 0;
    }
  }
  .spinner {
    margin: auto;
  }
  .gray-text {
    margin-left: 5px;
  }
  .webhook-input {
    width: 338px;
    margin-left: 111px;
    margin-bottom: 19px;
  }

  .icon-chrome,
  .icon-firefox {
    width: 24px;
    height: 24px;
  }
  .icon-chrome {
    margin-right: 7px;
  }
  .tooltip-icons {
    margin-bottom: 8px;
    margin-left: 1px;
  }
  .line-break {
    width: 100%;
  }
  @media only screen and (max-width : 1279px) {
    .coin-info {
      flex-wrap: wrap;
      margin-bottom: 22px;
      &__col_selector {
        width: 100%;
        border: none;
        margin-bottom: 10px;
      }
      &__col:nth-child(2) {
        border-radius: $table-border-radius 0 0 $table-border-radius;
      }
    }
    .fields {
      &_stage1 {
        // justify-content: space-between;
      }
      &__col1 {
        // margin-right: 0;
        margin-right: 55px;
      }
    }
  }
  @media only screen and (max-width : 1042px) {
    .line-break {
      display: none;
    }
    .fields_stage2 {
      flex-direction: column;
      padding-bottom: 0;
      margin-bottom: 17px;
    }
    .channels {
      order: 1;
    }
    .type {
      order: 3;
      margin: 20px 0 0;
      &__items {
        margin-left: 69px;
      }
    }
    .webhook-input {
      order: 2;
    }
    .alert {
      margin-bottom: 60px;
    }
  }
  @media only screen and (max-width : 992px) {
    .body-block_stage1 {
      padding: 0;
      .preview {
        width: calc(100% - 53px);
        margin: 16px 32px 10px;
      }
    }
    .footer-block {
      padding: 20px 30px 61px;
    }
    .coin-info {
      &__col {
        &:nth-child(2) {
          border-left: none;
          border-radius: 0;
          width: 33%;
        }
        &:nth-child(3) {
          width: 41%;
        }
        &:nth-child(4) {
          border-right: none;
          border-radius: 0;
          width: 26%;
        }

      }
    }
  }
  @media only screen and (max-width : 832px) {
    .fields {
      $fieldsSelector: &;
      &_stage1 {
        flex-direction: column;
        align-items: flex-start;
        #{$fieldsSelector}__col2 {
          flex-direction: column-reverse;
          justify-content: flex-start;
          margin-top: 13px;
        }
        #{$fieldsSelector}__item_target-value {
          margin-top: 0px;
          margin-bottom: 31px;
        }
      }
    }
  }
  @media only screen and (max-width : 800px) {
    .channels__col {
      padding-left: 39px;
    }
    .channels__items {
      flex-direction: column;
    }
    .type {
      margin-bottom: 23px;
    }
  }
  @media only screen and (max-width : 753px) {
    .coin-info {
      &__col {
        &:nth-child(2),
        &:nth-child(3),
        &:nth-child(4) {
          width: 100%;
          border-left: none;
          border-top: none;
        }
        &_selector {
          margin-bottom: -5px;
        }
      }
    }
    .fields_stage1 {
      padding-top: 38px;
    }
  }
  @media only screen and (max-width : 582px) {
    .channels {
      flex-direction: column;
      margin-bottom: 31px;
      &__col {
        padding-left: 9px;
      }
      &__items {
        margin-top: 14px;
      }
    }
    .type {
      flex-direction: column;
      &__items {
        margin-left: 0;
        margin-top: 31px;
      }
    }
    .webhook-input {
      margin: 0 0 9px;
    }
    .fields_stage2 {
      margin-bottom: 37px;
    }
  }
  @media only screen and (max-width : 502px) {
    .alert {
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 31px 30px;
      &__btn {
        margin: 28px 0 0
      }
      &__text {
        text-align: center;
      }
    }
  }
  @media only screen and (max-width : 474px) {
    .buttons {
      display: flex;
      flex-direction: column;
      align-items: center;
      &__item:not(:first-child) {
        @include incrSelectorWeight(1) {
          margin-top: 15px;
        }
      }
      &__item,
      &__item-link {
        width: 180px;
        margin-right: 0;
      }
      &__item:not(:last-child) {
        margin-right: 0;
      }
    }
    .fields__col1 {
      margin-right: 0;
    }
  }
  @media only screen and (max-width : 432px) {
    .wrap {
      padding: 10px 0;
    }
    $padding-left: 34px;
    .coin-info {
      &__col {
        padding-left: $padding-left;
        &_selector {
          padding-left: 16px;
        }
      }
    }
    .fields {
      $fieldsSelector: &;
      &_stage1 {
        padding-left: $padding-left;
        #{$fieldsSelector}__col1 {
          width: 100%;
        }
      }
      &_stage2 {
        padding-top: 2px;
      }
    }
    .webhook-input {
      width: 100%;
    }
    .body-block_stage2 {
      padding-left: 15px;
      padding-right: 15px;
    }
  }
  @media only screen and (max-width : 345px) {
    .fields {
      overflow-x: hidden;
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/pages/alertCreation/index.vue